import { shapeGeometries } from '@/constants/shapes'
import type {
  TShapeAppearance,
  TShapeCommand,
  TShapeHead,
  TShapeKind,
  TShapeLineEnd,
  TShapeParts,
  TShapePoint,
  TShapePptxPoint,
  TShapeRoundedCorner,
  TShapeStrokeStyle,
} from '@/types/shapes'

const headLengthRatio = 3.5
const headHalfWidthRatio = 1.75
const circleKappa = 0.5522847498
const angleEpsilon = 1e-3

function add(a: TShapePoint, b: TShapePoint): TShapePoint {
  return { x: a.x + b.x, y: a.y + b.y }
}

function subtract(a: TShapePoint, b: TShapePoint): TShapePoint {
  return { x: a.x - b.x, y: a.y - b.y }
}

function scale(a: TShapePoint, factor: number): TShapePoint {
  return { x: a.x * factor, y: a.y * factor }
}

function length(a: TShapePoint): number {
  return Math.hypot(a.x, a.y)
}

function direction(from: TShapePoint, to: TShapePoint): TShapePoint {
  const delta = subtract(to, from)
  const size = length(delta)

  return size ? scale(delta, 1 / size) : { x: 1, y: 0 }
}

export function isLineShape(kind: TShapeKind): boolean {
  return kind === 'line'
}

export function isOpenShape(kind: TShapeKind): boolean {
  return shapeGeometries[kind].type === 'open'
}

export function shapeSupportsRadius(kind: TShapeKind): boolean {
  const geometry = shapeGeometries[kind]

  return geometry.type === 'polygon' || (geometry.type === 'open' && geometry.points.length > 2)
}

export function shapeHasFill(kind: TShapeKind): boolean {
  return !isOpenShape(kind)
}

export function shapeStrokeWidth(appearance: TShapeAppearance): number {
  if (!appearance.strokeColor || !appearance.strokeWidth) return 0

  return Math.max(0, appearance.strokeWidth)
}

function roundedCorner(
  previous: TShapePoint,
  point: TShapePoint,
  next: TShapePoint,
  radius: number,
): TShapeRoundedCorner {
  const toPrevious = subtract(previous, point)
  const toNext = subtract(next, point)
  const previousLength = length(toPrevious)
  const nextLength = length(toNext)

  if (radius <= 0 || !previousLength || !nextLength) return { start: point, end: point }

  const previousDirection = scale(toPrevious, 1 / previousLength)
  const nextDirection = scale(toNext, 1 / nextLength)
  const cosine = Math.min(
    1,
    Math.max(-1, previousDirection.x * nextDirection.x + previousDirection.y * nextDirection.y),
  )
  const angle = Math.acos(cosine)

  if (angle < angleEpsilon || Math.PI - angle < angleEpsilon) return { start: point, end: point }

  const tanHalf = Math.tan(angle / 2)
  const cut = Math.min(radius / tanHalf, previousLength / 2, nextLength / 2)
  const handle = (4 / 3) * Math.tan((Math.PI - angle) / 4) * cut * tanHalf
  const start = add(point, scale(previousDirection, cut))
  const end = add(point, scale(nextDirection, cut))

  return {
    start,
    end,
    control1: subtract(start, scale(previousDirection, handle)),
    control2: subtract(end, scale(nextDirection, handle)),
  }
}

function cornerCommands(corner: TShapeRoundedCorner): TShapeCommand[] {
  const line: TShapeCommand = { type: 'line', x: corner.start.x, y: corner.start.y }

  if (!corner.control1 || !corner.control2) return [line]

  return [
    line,
    {
      type: 'cubic',
      x1: corner.control1.x,
      y1: corner.control1.y,
      x2: corner.control2.x,
      y2: corner.control2.y,
      x: corner.end.x,
      y: corner.end.y,
    },
  ]
}

export function roundPolygon(points: TShapePoint[], radius: number): TShapeCommand[] {
  const count = points.length

  if (count < 3) return []

  const corners = points.map((point, index) =>
    roundedCorner(points[(index - 1 + count) % count], point, points[(index + 1) % count], radius),
  )

  const commands: TShapeCommand[] = [{ type: 'move', x: corners[0].end.x, y: corners[0].end.y }]

  for (let index = 1; index <= count; index++) {
    commands.push(...cornerCommands(corners[index % count]))
  }

  commands.push({ type: 'close' })

  return commands
}

export function roundPolyline(points: TShapePoint[], radius: number): TShapeCommand[] {
  if (points.length < 2) return []

  const commands: TShapeCommand[] = [{ type: 'move', x: points[0].x, y: points[0].y }]

  for (let index = 1; index < points.length - 1; index++) {
    commands.push(...cornerCommands(roundedCorner(points[index - 1], points[index], points[index + 1], radius)))
  }

  const last = points[points.length - 1]

  commands.push({ type: 'line', x: last.x, y: last.y })

  return commands
}

function placePoint(
  point: TShapePoint,
  width: number,
  height: number,
  inset: number,
  appearance: TShapeAppearance,
): TShapePoint {
  const x = appearance.flipH ? 1 - point.x : point.x
  const y = appearance.flipV ? 1 - point.y : point.y

  return {
    x: inset + x * Math.max(0, width - inset * 2),
    y: inset + y * Math.max(0, height - inset * 2),
  }
}

function placeCommand(command: TShapeCommand, place: (point: TShapePoint) => TShapePoint): TShapeCommand {
  if (command.type === 'close') return command

  const end = place(command)

  if (command.type !== 'cubic') return { type: command.type, x: end.x, y: end.y }

  const control1 = place({ x: command.x1, y: command.y1 })
  const control2 = place({ x: command.x2, y: command.y2 })

  return { type: 'cubic', x1: control1.x, y1: control1.y, x2: control2.x, y2: control2.y, x: end.x, y: end.y }
}

function circleCommands(center: TShapePoint, radius: number): TShapeCommand[] {
  const handle = radius * circleKappa
  const { x, y } = center

  return [
    { type: 'move', x: x + radius, y },
    { type: 'cubic', x1: x + radius, y1: y + handle, x2: x + handle, y2: y + radius, x, y: y + radius },
    { type: 'cubic', x1: x - handle, y1: y + radius, x2: x - radius, y2: y + handle, x: x - radius, y },
    { type: 'cubic', x1: x - radius, y1: y - handle, x2: x - handle, y2: y - radius, x, y: y - radius },
    { type: 'cubic', x1: x + handle, y1: y - radius, x2: x + radius, y2: y - handle, x: x + radius, y },
    { type: 'close' },
  ]
}

function polylineCommands(points: TShapePoint[], closed: boolean): TShapeCommand[] {
  const commands: TShapeCommand[] = points.map((point, index) => ({
    type: index === 0 ? 'move' : 'line',
    x: point.x,
    y: point.y,
  }))

  if (closed) commands.push({ type: 'close' })

  return commands
}

function lineHead(
  type: TShapeLineEnd,
  tip: TShapePoint,
  direction: TShapePoint,
  strokeWidth: number,
): TShapeHead | null {
  const headLength = strokeWidth * headLengthRatio
  const halfWidth = strokeWidth * headHalfWidthRatio
  const normal = { x: -direction.y, y: direction.x }
  const back = subtract(tip, scale(direction, headLength))

  if (type === 'arrow') {
    return {
      commands: polylineCommands(
        [add(back, scale(normal, halfWidth)), tip, subtract(back, scale(normal, halfWidth))],
        false,
      ),
      filled: false,
    }
  }

  if (type === 'triangle') {
    return {
      commands: polylineCommands(
        [tip, add(back, scale(normal, halfWidth)), subtract(back, scale(normal, halfWidth))],
        true,
      ),
      filled: true,
    }
  }

  if (type === 'diamond') {
    return {
      commands: polylineCommands(
        [
          add(tip, scale(direction, headLength / 2)),
          add(tip, scale(normal, halfWidth)),
          subtract(tip, scale(direction, headLength / 2)),
          subtract(tip, scale(normal, halfWidth)),
        ],
        true,
      ),
      filled: true,
    }
  }

  if (type === 'circle') return { commands: circleCommands(tip, halfWidth), filled: true }

  return null
}

function shaftEnd(type: TShapeLineEnd, tip: TShapePoint, outward: TShapePoint, strokeWidth: number): TShapePoint {
  if (type !== 'triangle') return tip

  return subtract(tip, scale(outward, strokeWidth * headLengthRatio * 0.9))
}

function openParts(
  points: TShapePoint[],
  width: number,
  height: number,
  strokeWidth: number,
  appearance: TShapeAppearance,
): TShapeParts {
  const startType = appearance.lineStart ?? 'none'
  const endType = appearance.lineEnd ?? 'none'
  const hasHeads = strokeWidth > 0 && (startType !== 'none' || endType !== 'none')
  const inset = hasHeads ? strokeWidth * headHalfWidthRatio : strokeWidth / 2
  const placed = points.map((point) => placePoint(point, width, height, inset, appearance))
  const first = placed[0]
  const last = placed[placed.length - 1]
  const startOutward = direction(placed[1], first)
  const endOutward = direction(placed[placed.length - 2], last)

  const shaft = [
    shaftEnd(startType, first, startOutward, strokeWidth),
    ...placed.slice(1, -1),
    shaftEnd(endType, last, endOutward, strokeWidth),
  ]

  const heads = [
    lineHead(startType, first, startOutward, strokeWidth),
    lineHead(endType, last, endOutward, strokeWidth),
  ]

  return {
    body: roundPolyline(shaft, appearance.radius ?? 0),
    closed: false,
    heads: strokeWidth > 0 ? heads.filter((head): head is TShapeHead => head !== null) : [],
  }
}

export function buildShapeParts(
  kind: TShapeKind,
  width: number,
  height: number,
  appearance: TShapeAppearance,
): TShapeParts {
  const geometry = shapeGeometries[kind]
  const strokeWidth = shapeStrokeWidth(appearance)

  if (geometry.type === 'open') return openParts(geometry.points, width, height, strokeWidth, appearance)

  const place = (point: TShapePoint) => placePoint(point, width, height, strokeWidth / 2, appearance)

  if (geometry.type === 'curve') {
    return { body: geometry.commands.map((command) => placeCommand(command, place)), closed: true, heads: [] }
  }

  return { body: roundPolygon(geometry.points.map(place), appearance.radius ?? 0), closed: true, heads: [] }
}

function formatNumber(value: number): string {
  return String(Math.round(value * 100) / 100)
}

export function shapeCommandsToSvgPath(commands: TShapeCommand[]): string {
  return commands
    .map((command) => {
      if (command.type === 'close') return 'Z'

      if (command.type === 'cubic') {
        return `C${[command.x1, command.y1, command.x2, command.y2, command.x, command.y].map(formatNumber).join(' ')}`
      }

      return `${command.type === 'move' ? 'M' : 'L'}${formatNumber(command.x)} ${formatNumber(command.y)}`
    })
    .join(' ')
}

export function shapeCommandsToPptxPoints(commands: TShapeCommand[], unitToInch: number): TShapePptxPoint[] {
  return commands.map((command) => {
    if (command.type === 'close') return { close: true }

    const x = command.x * unitToInch
    const y = command.y * unitToInch

    if (command.type === 'move') return { x, y, moveTo: true }

    if (command.type === 'line') return { x, y }

    return {
      x,
      y,
      curve: {
        type: 'cubic',
        x1: command.x1 * unitToInch,
        y1: command.y1 * unitToInch,
        x2: command.x2 * unitToInch,
        y2: command.y2 * unitToInch,
      },
    }
  })
}

export function shapeStrokeDashArray(style: TShapeStrokeStyle | undefined, strokeWidth: number): string | undefined {
  if (!strokeWidth) return undefined

  if (style === 'dashed') return `${strokeWidth * 3} ${strokeWidth * 2}`

  if (style === 'dotted') return `0 ${strokeWidth * 2}`

  return undefined
}

export function shapeStrokeLineCap(style: TShapeStrokeStyle | undefined): 'round' | 'butt' {
  return style === 'dotted' ? 'round' : 'butt'
}
